import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Home, Clock, Route } from "lucide-react";
import { areas as landingAreas } from "../data/areas";

const landingByName = Object.fromEntries(landingAreas.map((a) => [a.name, a.path]));
const Chip = ({ area, className }) =>
  landingByName[area] ? (
    <Link to={landingByName[area]} data-testid={`area-chip-${landingByName[area].slice(1)}`} className={`${className} underline-offset-4 hover:underline`}>
      {area}
    </Link>
  ) : (
    <span className={className}>{area}</span>
  );

const areas = {
  primary: [
    "Λαμία",
    "Στυλίδα",
    "Γοργοπόταμος",
    "Λιανοκλάδι",
    "Ροδίτσα",
    "Ανθήλη",
    "Αγία Παρασκευή",
    "Καλύβια",
    "Νέο Κρίκελλο",
    "Κόμμα",
    "Ξηριάς",
  ],
  secondary: [
    "Δομοκός",
    "Μακρακώμη",
    "Σπερχειάδα",
    "Αταλάντη",
    "Αμφίκλεια",
    "Ελάτεια",
    "Λιβανάτες",
    "Μαρτίνο",
    "Καμένα Βούρλα",
    "Άγιος Κωνσταντίνος",
    "Μώλος",
    "Αγία Μαρίνα",
    "Πελασγία",
    "Μαλεσίνα",
  ],
};

const ServiceAreas = () => {
  return (
    <section id="areas" className="py-24 relative">
      <div className="absolute inset-0 circuit-dots opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto reveal">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/80">
            Περιοχές εξυπηρέτησης
          </div>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Καλύπτουμε όλη τη <span className="text-emerald-400">Φθιώτιδα</span>
          </h2>
          <p className="mt-4 text-emerald-50/70">
            Κατ’ οίκον επισκευή σε Λαμία και γύρω περιοχές. Ηλεκτρονικές συσκευές
            παρακολούθηση & επισκευή από εμάς, χωρίς εσείς να μετακινηθείτε.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <div className="pcb-card p-5 reveal">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                <Home className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-white font-semibold">Κατ’ οίκον εξυπηρέτηση</div>
            </div>
            <p className="mt-3 text-sm text-emerald-50/75">
              Ερχόμαστε στο χώρο σας με πλήρη εξοπλισμό — χωρίς να χρειαστεί να
              μετακινηθείτε.
            </p>
          </div>
          <div className="pcb-card p-5 reveal">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-white font-semibold">24/7 διαθέσιμοι</div>
            </div>
            <p className="mt-3 text-sm text-emerald-50/75">
              Τηλεφωνήστε μας οποιαδήποτε ώρα της ημέρας — απαντάμε άμεσα και προγραμματίζουμε
              επίσκεψη.
            </p>
          </div>
          <div className="pcb-card p-5 reveal">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                <Route className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-white font-semibold">Δωρεάν μετακίνηση</div>
            </div>
            <p className="mt-3 text-sm text-emerald-50/75">
              Για επισκευές άνω των 50€ εντός Λαμίας — χωρίς χρέωση μετακίνησης.
            </p>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="pcb-card p-6 reveal">
            <div className="flex items-center gap-2 text-emerald-300">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-semibold">Κοντινές περιοχές (10–30′)</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {areas.primary.map((area) => (
                <Chip
                  key={area}
                  area={area}
                  className="px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-500/20 transition-colors"
                />
              ))}
            </div>
          </div>
          <div className="pcb-card p-6 reveal">
            <div className="flex items-center gap-2 text-emerald-300">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-semibold">Ευρύτερη Φθιώτιδα (30–60′)</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {areas.secondary.map((area) => (
                <Chip
                  key={area}
                  area={area}
                  className="px-3 py-1.5 rounded-md bg-[#04101c] border border-emerald-900/70 text-emerald-100/80 text-sm hover:border-emerald-500/40 transition-colors"
                />
              ))}
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-emerald-100/50">
          Η περιοχή σας δεν εμφανίζεται;{" "}
          <a href="#contact" className="text-emerald-300 hover:text-emerald-200 underline">
            Τηλεφωνήστε μας
          </a>{" "}— συχνά εξυπηρετούμε και εκτός λίστας.
        </p>
      </div>
    </section>
  );
};

export default ServiceAreas;
